import React, { forwardRef, useMemo, useCallback } from 'react'
import { TableRow, TableCell, Link as MuiLink } from '@mui/material'
import { Link } from 'react-router-dom'
import { BaseBlockProps, ChartFormat } from '../types'
import { useLinkChartData } from '../../../hooks'
import {
    getUniqueFromData,
    getGroupMode,
    getChartLayout,
    getSchemeOrDefault,
    isDateOriented,
    getBlockStyleConfig,
    navigateToDossier,
} from '../../../utils'
import {
    BlockWrapper,
    BlockHeader,
    BlockToolbar,
    LoadingState,
    EmptyState,
    ChartContainer,
    DataTable,
} from '../base'
import { PieChart, BarChart, LineChart } from '../../../Components/Display'

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface CountLinkBlockProps extends BaseBlockProps {}

export const CountLinkBlock = forwardRef<HTMLDivElement, CountLinkBlockProps>(
    (
        {
            kedo,
            block,
            dashboard,
            container,
            onClose,
            onMax,
            onSuccess,
            getBlockHeader,
            dossier,
            link,
        },
        ref
    ) => {
        const {
            rawData,
            transformedData,
            originField,
            loading,
            dateFormat,
            setDateFormat,
            refetch,
        } = useLinkChartData({
            kedo,
            block,
            dossier,
            link,
        })

        const handleFilterChange = useCallback(
            (key: string, value: string) => {
                if (key === 'dateFormat') {
                    setDateFormat(value as 'year_month' | 'quarter' | 'year')
                }
            },
            [setDateFormat]
        )

        const chartFormat = block.settings?.chart_format as ChartFormat
        const isDate = isDateOriented(block)
        const scheme = getSchemeOrDefault(block.settings?.scheme)
        const { containerStyle, className } = getBlockStyleConfig(
            dashboard,
            container,
            false,
            chartFormat,
            rawData?.length ?? 0
        )

        const handlePieClick = useCallback(
            (node: { data: { kedoId?: number } }) => {
                if (node?.data?.kedoId) {
                    navigateToDossier(node.data.kedoId)
                }
            },
            []
        )

        const getTranslation = useCallback(
            (value: string | number) => {
                if (!originField?.def_field?.def_field_lists)
                    return String(value)

                const listItem = originField.def_field.def_field_lists.find(
                    (item) => item.id === Number(value)
                )

                if (listItem) {
                    return kedo.env().translateItem(listItem, 'def_field_list')
                }

                return String(value)
            },
            [originField, kedo]
        )

        const renderDateTable = () => {
            if (!rawData) return null

            const typedData = rawData as Array<Record<string, unknown>>
            let yKey = 'summary'
            let xKey = 'yearAnddate'

            if (block.settings?.reverse_axis === 'yes') {
                xKey = 'summary'
                yKey = 'yearAnddate'
            }

            const yItems = getUniqueFromData(typedData, yKey)
            const xItems = getUniqueFromData(typedData, xKey)

            const columns = [
                { id: 'row', label: '' },
                ...xItems.map((x) => ({ id: x, label: x })),
            ]

            const tableRows = yItems.map((yItem) => {
                const row: Record<string, unknown> = { row: yItem }
                const linkItem = typedData.find((d) => d[yKey] === yItem)

                if (linkItem?.['2']) {
                    row.row = (
                        <Link to={`/contentdossier/${linkItem['2']}`}>
                            {yItem}
                        </Link>
                    )
                }

                xItems.forEach((xItem) => {
                    const found = typedData.find(
                        (d) => d[xKey] === xItem && String(d[yKey]) === yItem
                    )
                    row[xItem] = found ? found['1'] : 0
                })

                return row
            })

            return <DataTable columns={columns} data={tableRows} />
        }

        const renderNonDateTable = () => {
            if (!rawData || !originField) {
                return <LoadingState />
            }

            const typedData = rawData as Array<Record<string, unknown>>
            const xKey = 'summary'
            const yKey = 'summary2'
            const vKey = '1'

            const uniqueX = getUniqueFromData(typedData, xKey)
            const uniqueY = getUniqueFromData(typedData, yKey)

            const columns = [
                { id: 'row', label: '' },
                ...uniqueX.map((x) => ({
                    id: x,
                    label: getTranslation(x),
                })),
                { id: 'other', label: kedo.t('Other') },
                { id: 'total', label: kedo.t('Total') },
            ]

            const tableRows = uniqueY.map((yItem) => {
                const row: Record<string, unknown> = {
                    row: getTranslation(yItem),
                }

                uniqueX.forEach((xItem) => {
                    const filtered = typedData.filter(
                        (d) =>
                            d[xKey] === xItem &&
                            d[yKey] !== null &&
                            String(d[yKey]) === String(yItem)
                    )
                    row[xItem] = filtered.reduce(
                        (sum, d) => sum + Number(d[vKey]),
                        0
                    )
                })

                const yFiltered = typedData.filter(
                    (d) => d[yKey] !== null && String(d[yKey]) === String(yItem)
                )
                row.other = yFiltered.find((d) => d.summary === null)?.[1] ?? 0
                row.total = yFiltered.reduce(
                    (sum, d) => sum + Number(d[vKey]),
                    0
                )

                return row
            })

            const footerTotal = typedData
                .filter((d) => d[yKey] !== null)
                .reduce((sum, d) => sum + Number(d[vKey]), 0)

            const footer = (
                <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>
                        {kedo.t('Total')}
                    </TableCell>
                    {uniqueX.map((x) => (
                        <TableCell key={x}>
                            {typedData
                                .filter(
                                    (d) => d[xKey] === x && d[yKey] !== null
                                )
                                .reduce((sum, d) => sum + Number(d[vKey]), 0)}
                        </TableCell>
                    ))}
                    <TableCell />
                    <TableCell sx={{ fontWeight: 700 }}>
                        {footerTotal}
                    </TableCell>
                </TableRow>
            )

            return (
                <DataTable columns={columns} data={tableRows} footer={footer} />
            )
        }

        const renderChart = useMemo(() => {
            if (!transformedData || !rawData || rawData.length === 0) {
                return <EmptyState message={kedo.t('No items found')} />
            }

            if (isDate) {
                if (chartFormat === 'table') {
                    return renderDateTable()
                }

                if (chartFormat === 'bar') {
                    return (
                        <BarChart
                            data={transformedData.barData}
                            keys={transformedData.keys}
                            colors={{ scheme }}
                            groupMode={getGroupMode(block.settings)}
                            layout={getChartLayout(block.settings)}
                            kedo={kedo}
                        />
                    )
                }

                return (
                    <LineChart
                        data={transformedData.lineData}
                        colors={{ scheme }}
                        kedo={kedo}
                    />
                )
            }

            if (chartFormat === 'table') {
                return renderNonDateTable()
            }

            if (chartFormat === 'bar') {
                return (
                    <BarChart
                        data={
                            transformedData.barData.length > 0
                                ? transformedData.barData
                                : []
                        }
                        keys={['value']}
                        indexBy="id"
                        colors={{ scheme }}
                        groupMode={getGroupMode(block.settings)}
                        layout={getChartLayout(block.settings)}
                        kedo={kedo}
                    />
                )
            }

            if (chartFormat === 'line') {
                return (
                    <LineChart
                        data={transformedData.lineData}
                        colors={{ scheme }}
                        kedo={kedo}
                    />
                )
            }

            return (
                <PieChart
                    data={transformedData.pieData}
                    colors={{ scheme }}
                    onClick={handlePieClick}
                    innerRadius={0.8}
                    enableArcLinkLabels={false}
                    enableArcLabels={false}
                />
            )
        }, [
            transformedData,
            rawData,
            isDate,
            chartFormat,
            scheme,
            block.settings,
            kedo,
            handlePieClick,
            renderDateTable,
            renderNonDateTable,
        ])

        return (
            <BlockWrapper ref={ref} dashboard={dashboard} sx={containerStyle}>
                <BlockHeader>
                    <BlockToolbar
                        kedo={kedo}
                        block={block}
                        onClose={onClose}
                        onMax={onMax}
                        onSuccess={onSuccess}
                        fetchData={refetch}
                        setFilterAndFetchData={handleFilterChange}
                    />
                    {getBlockHeader?.(block, dossier)}
                </BlockHeader>
                {loading ? (
                    <LoadingState />
                ) : (
                    <ChartContainer height={containerStyle.height}>
                        {renderChart}
                    </ChartContainer>
                )}
            </BlockWrapper>
        )
    }
)

CountLinkBlock.displayName = 'CountLinkBlock'
